import { useEffect, useMemo, useRef, useState } from 'react';
import { CircleAlert, Plus } from 'lucide-react';
import { cn } from '../lib/utils';
import { STEP_TYPES, CATEGORY_ORDER, StepDefinition, TONE_CLASSES } from './nodes/stepRegistry';

interface TooltipState {
  text: string;
  top: number;
  right: number;
}

interface StepPickerProps {
  onAddNode: (type: string) => void;
  onAddPreset: (presetId: string) => void;
}

export function StepPicker({ onAddNode, onAddPreset }: StepPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  function showTooltip(e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>, text: string) {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, top: rect.top, right: window.innerWidth - rect.left + 8 });
  }

  useEffect(() => {
    if (!open) { setTooltip(null); return; }
    searchRef.current?.focus();

    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q ? STEP_TYPES.filter(s => s.label.toLowerCase().includes(q)) : STEP_TYPES;
    return CATEGORY_ORDER
      .map(category => ({ category, items: matches.filter(s => s.category === category) }))
      .filter(g => g.items.length > 0);
  }, [query]);

  function handleSelect(step: StepDefinition) {
    if (step.type) onAddNode(step.type);
    else if (step.presetId) onAddPreset(step.presetId);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-[13px] font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
      >
        <Plus size={15} className={cn('transition-transform', open && 'rotate-45')} />
        Add step
      </button>

      {open && (
        <div className="absolute left-0 mt-1.5 z-50 w-80 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-popover shadow-xl">
          <div className="sticky top-0 bg-popover p-2 border-b border-border">
            <input
              ref={searchRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              className="h-8 w-full rounded-md border border-input bg-card px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {grouped.length === 0 && (
            <div className="px-3 py-3 text-xs text-muted-foreground">No matches</div>
          )}

          {grouped.map(group => (
            <div key={group.category}>
              <div className="px-3 pt-3 pb-1 text-xs font-medium text-muted-foreground">
                {group.category}
              </div>
              {group.items.map(step => {
                const Icon = step.icon;
                return (
                  <div
                    key={step.type ?? step.presetId}
                    className="mx-1 flex items-center rounded-md hover:bg-accent"
                  >
                    <button
                      onClick={() => handleSelect(step)}
                      className="flex-1 flex items-center gap-2.5 px-2 py-1.5 text-sm text-left"
                    >
                      <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md', TONE_CLASSES[step.tone].tile)}>
                        <Icon size={15} />
                      </span>
                      {step.label}
                    </button>
                    <span
                      tabIndex={0}
                      aria-label={`What does ${step.label} do?`}
                      onMouseEnter={e => showTooltip(e, step.description)}
                      onMouseLeave={() => setTooltip(null)}
                      onFocus={e => showTooltip(e, step.description)}
                      onBlur={() => setTooltip(null)}
                      className="px-2 py-1.5 text-muted-foreground hover:text-primary cursor-help shrink-0"
                    >
                      <CircleAlert size={14} />
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {tooltip && (
        <div
          role="tooltip"
          style={{ position: 'fixed', top: tooltip.top, right: tooltip.right, maxWidth: 260 }}
          className="z-[60] rounded-md bg-popover text-popover-foreground border border-border text-xs leading-relaxed px-3 py-2 shadow-lg pointer-events-none"
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
